import type { FieldsProxy } from "@shared/helpers/sql-relations";
import { useEffect, useMemo, type PropsWithChildren } from "react";
import { repo } from "remult";
import { useHyperTable } from "../../HyperTable";
import { createFieldProxyForEntity } from "../../functions";
import type {
  FieldId,
  HyperTableEntityFilter as HyperTableEntityFilterType,
  HyperTableFilterBoth,
} from "../../types";
import { useToolbarFilters } from "./HyperTableToolbar";
import { getFinalChildEntityFromFieldPath } from "./filter-types/helpers";
import { markHyperTableFilterComponent } from "./is-filter-child";

interface HyperTableEntityFilterProps<Entity>
  extends Omit<HyperTableFilterBoth<Entity>, "caption">,
    Omit<HyperTableEntityFilterType<Entity>, "fieldName">,
    PropsWithChildren {
  /**
   * Description:
   * ---
   * The name of the field in the entity to filter on.
   * This is used to generate the filter UI and apply the filter to the query.
   *
   * ---
   *
   * Important:
   * ---
   * - **Required**: This must be a valid field name in the entity.
   * - **Literal Fields Only**: Use only for fields that are literals or relations (e.g., strings, numbers, toOne, toMany) for more complex field types use `CustomFilter`
   */
  fieldName:
    | ((entity: FieldsProxy<Entity>) => FieldId<Entity>)
    | FieldId<Entity>;
}

function HyperTableEntityFilterComponent<Entity extends any>(
  props: HyperTableEntityFilterProps<Entity>,
) {
  const { fieldName, children, onChange, getOptionCaption, ...rest } = props;

  const { filterActions } = useToolbarFilters<Entity>();
  const { getEntity } = useHyperTable<Entity>();

  const fieldnameString = useMemo(() => {
    if (typeof fieldName === "string") {
      return fieldName;
    }

    const entityFields = createFieldProxyForEntity(getEntity());
    return fieldName(entityFields);
  }, [fieldName, getEntity]);

  const filter = useMemo(() => {
    return {
      caption:
        children ??
        repo(
          getFinalChildEntityFromFieldPath(getEntity(), fieldnameString),
        ).fields.find(fieldnameString.split(".").pop()!)?.caption,
      fieldName: fieldnameString,
      ...rest,
    };
  }, [
    children,
    onChange,
    getOptionCaption,
    fieldnameString,
    JSON.stringify(rest),
  ]);

  useEffect(() => {
    if (filterActions.has(filter)) filterActions.update(filter);
  }, [filter]);

  useEffect(() => {
    filterActions.add(filter);
    return () => {
      filterActions.remove(filter);
    };
  }, []);

  return null;
}

export const HyperTableEntityFilter = markHyperTableFilterComponent(
  HyperTableEntityFilterComponent,
);
